import { useEffect, useState, useCallback } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { usePromptDialog } from '../hooks/usePromptDialog';
import {
  fetchUserGroupsList,
  createUserGroup,
  renameUserGroup,
  deleteUserGroup,
  fetchGroupMembers,
  addGroupMember,
  removeGroupMember,
  fetchMemberPickerUsers,
  fetchMyGroupMemberships,
  MemberPickerUser,
  TreeNode,
  AdminGroupRow,
  GroupMemberRow,
  GroupMembershipSummary,
} from '../features/userGroups/userGroupsApi';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';

const inputClass =
  'w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30';
const btnSecondary =
  'rounded-xl border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/80';
const btnPrimary =
  'rounded-xl bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90';
const btnDanger = 'rounded-xl px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10';

function TreeRows({
  nodes,
  depth,
  readOnly,
  onAddChild,
  onRename,
  onDelete,
  onManageMembers,
}: {
  nodes: TreeNode[];
  depth: number;
  readOnly: boolean;
  onAddChild: (parentId: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onManageMembers: (id: string) => void;
}) {
  const { confirm } = useConfirmDialog();
  const { prompt } = usePromptDialog();
  return (
    <>
      {nodes.map((n) => (
        <div key={n.id} className="border-b border-border/60 last:border-0">
          <div
            className="flex flex-wrap items-center gap-2 py-3"
            style={{ paddingLeft: `${depth * 18}px` }}
          >
            <span className="min-w-0 flex-1 text-sm font-medium text-foreground">{n.name}</span>
            <span className="text-xs tabular-nums text-muted-foreground">{n.memberCount} miembros</span>
            <button type="button" className={btnSecondary} onClick={() => onManageMembers(n.id)}>
              Miembros
            </button>
            {!readOnly && (
              <>
                <button type="button" className={btnSecondary} onClick={() => onAddChild(n.id)}>
                  Subgrupo
                </button>
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={async () => {
                    const name = await prompt({
                      title: 'Renombrar grupo',
                      label: 'Nuevo nombre',
                      defaultValue: n.name,
                      confirmText: 'Guardar',
                      required: true,
                    });
                    if (name) onRename(n.id, name);
                  }}
                >
                  Renombrar
                </button>
                <button
                  type="button"
                  className={btnDanger}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Eliminar grupo',
                      message: '¿Eliminar grupo y subgrupos?',
                      confirmText: 'Eliminar',
                      variant: 'danger',
                    });
                    if (ok) onDelete(n.id);
                  }}
                >
                  Eliminar
                </button>
              </>
            )}
          </div>
          {n.children?.length > 0 && (
            <TreeRows
              nodes={n.children}
              depth={depth + 1}
              readOnly={readOnly}
              onAddChild={onAddChild}
              onRename={onRename}
              onDelete={onDelete}
              onManageMembers={onManageMembers}
            />
          )}
        </div>
      ))}
    </>
  );
}

export function UserGroupsPage() {
  const { canEdit: canManageGroups } = usePlatformAccessLevel('user_groups.manage_own');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [adminRows, setAdminRows] = useState<AdminGroupRow[]>([]);
  const [isAdminView, setIsAdminView] = useState(false);
  const [newRootName, setNewRootName] = useState('');
  const [membersGroupId, setMembersGroupId] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMemberRow[]>([]);
  const [parentForChild, setParentForChild] = useState<string | null>(null);
  const [childName, setChildName] = useState('');
  const [addUserId, setAddUserId] = useState('');
  const [pickerUsers, setPickerUsers] = useState<MemberPickerUser[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState('');
  const [memberships, setMemberships] = useState<GroupMembershipSummary[]>([]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [data, memberOf] = await Promise.all([
        fetchUserGroupsList(),
        fetchMyGroupMemberships().catch(() => [] as GroupMembershipSummary[]),
      ]);
      setMemberships(memberOf);
      if (data.view === 'all') {
        setIsAdminView(true);
        setAdminRows(data.items);
        setTree([]);
      } else {
        setIsAdminView(false);
        setTree(data.tree);
        setAdminRows([]);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar grupos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!membersGroupId) {
      setPickerUsers([]);
      setPickerError('');
      return;
    }
    (async () => {
      try {
        setMembers(await fetchGroupMembers(membersGroupId));
      } catch {
        setMembers([]);
      }
    })();
    if (!canManageGroups) {
      setPickerUsers([]);
      setPickerLoading(false);
      setPickerError('');
      return;
    }
    (async () => {
      setPickerLoading(true);
      setPickerError('');
      try {
        setPickerUsers(await fetchMemberPickerUsers());
      } catch (e: unknown) {
        setPickerUsers([]);
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (e instanceof Error ? e.message : 'No se pudo cargar el listado de usuarios');
        setPickerError(msg);
      } finally {
        setPickerLoading(false);
      }
    })();
  }, [membersGroupId, canManageGroups]);

  const handleCreateRoot = async () => {
    if (!newRootName.trim()) return;
    try {
      await createUserGroup({ name: newRootName.trim() });
      setNewRootName('');
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const handleCreateChild = async () => {
    if (!parentForChild || !childName.trim()) return;
    try {
      await createUserGroup({ name: childName.trim(), parentId: parentForChild });
      setChildName('');
      setParentForChild(null);
      await reload();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error');
    }
  };

  const readOnly = !canManageGroups;

  return (
    <div className="mx-auto max-w-4xl space-y-10 pb-8">
      <header className="space-y-2 border-b border-border/80 pb-8">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Organización</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Mis grupos</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          {readOnly
            ? 'Puedes consultar la estructura y los miembros de tus grupos. Los cambios están desactivados según tus permisos de plataforma.'
            : 'Crea equipos y asigna miembros para compartir encuestas por grupo. Abre un grupo y usa Miembros para añadir o quitar personas.'}
        </p>
      </header>

      {error && (
        <p className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}
      {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}

      {!loading && (
        <>
          {!readOnly && (
            <section className="rounded-2xl border border-border/80 bg-card/60 p-6 shadow-sm backdrop-blur-sm sm:p-8">
              <h2 className="text-sm font-semibold text-foreground">Nuevo grupo raíz</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {isAdminView
                  ? 'El grupo queda a tu nombre como propietario. Abajo ves todos los grupos del sistema.'
                  : 'Los subgrupos se crean desde cada fila del árbol.'}
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1">
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Nombre</label>
                  <input
                    className={inputClass}
                    value={newRootName}
                    onChange={(e) => setNewRootName(e.target.value)}
                    placeholder="Ej. Administrativos RRHH"
                  />
                </div>
                <button type="button" className={`${btnPrimary} shrink-0 px-5 py-2.5`} onClick={handleCreateRoot}>
                  Crear grupo
                </button>
              </div>
            </section>
          )}

          {!isAdminView && (
            <section className="rounded-2xl border border-border/80 bg-card/60 p-6 shadow-sm backdrop-blur-sm sm:p-8">
              <h2 className="text-lg font-semibold text-foreground">Tus grupos</h2>
              <div className="mt-4 overflow-hidden rounded-xl border border-border/60 bg-background/50">
                {tree.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">
                    {readOnly
                      ? 'No tienes grupos asignados o aún no hay datos que mostrar.'
                      : 'Aún no tienes grupos. Crea uno arriba para empezar.'}
                  </p>
                ) : (
                  <TreeRows
                    nodes={tree}
                    depth={0}
                    readOnly={readOnly}
                    onAddChild={(id) => setParentForChild(id)}
                    onRename={async (id, name) => {
                      try {
                        await renameUserGroup(id, name);
                        await reload();
                      } catch (e: unknown) {
                        alert(e instanceof Error ? e.message : 'Error');
                      }
                    }}
                    onDelete={async (id) => {
                      try {
                        await deleteUserGroup(id);
                        await reload();
                      } catch (e: unknown) {
                        alert(e instanceof Error ? e.message : 'Error');
                      }
                    }}
                    onManageMembers={(id) => setMembersGroupId(id)}
                  />
                )}
              </div>
            </section>
          )}

          {isAdminView && (
            <section className="overflow-hidden rounded-2xl border border-border/80 bg-card/60 shadow-sm backdrop-blur-sm">
              <div className="border-b border-border/60 px-6 py-4">
                <h2 className="text-lg font-semibold text-foreground">Todos los grupos</h2>
                <p className="text-xs text-muted-foreground">Vista superadmin</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/60 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <th className="px-6 py-3">Nombre</th>
                      <th className="px-6 py-3">Propietario</th>
                      <th className="px-6 py-3">Miembros</th>
                      <th className="px-6 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-10 text-center text-sm text-muted-foreground">
                          No hay grupos en el sistema todavía.
                        </td>
                      </tr>
                    ) : (
                      adminRows.map((r) => (
                        <tr key={r.id} className="border-b border-border/40 last:border-0">
                          <td className="px-6 py-3 font-medium text-foreground">{r.name}</td>
                          <td className="px-6 py-3 text-foreground">{r.owner.email}</td>
                          <td className="px-6 py-3 tabular-nums text-muted-foreground">{r._count.members}</td>
                          <td className="px-6 py-3">
                            <button type="button" className={btnSecondary} onClick={() => setMembersGroupId(r.id)}>
                              Miembros
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {memberships.length > 0 && (
            <section className="rounded-2xl border border-border/80 bg-card/60 p-6 shadow-sm backdrop-blur-sm sm:p-8">
              <h2 className="text-lg font-semibold text-foreground">Grupos donde participo</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Grupos creados por otros usuarios donde fuiste incluido como miembro (p. ej. destinatarios de encuestas).
              </p>
              <ul className="mt-4 divide-y divide-border overflow-hidden rounded-xl border border-border/60">
                {memberships.map((m) => (
                  <li key={m.groupId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                    <span className="font-medium text-foreground">{m.name}</span>
                    <span className="text-xs text-muted-foreground">Propietario: {m.owner.email}</span>
                    <button type="button" className={btnSecondary} onClick={() => setMembersGroupId(m.groupId)}>
                      Ver miembros
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {parentForChild && !readOnly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-6 shadow-lg">
            <h2 className="text-lg font-semibold text-foreground">Nuevo subgrupo</h2>
            <input
              className={inputClass}
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="Nombre del subgrupo"
            />
            <div className="flex justify-end gap-2">
              <button type="button" className={btnSecondary} onClick={() => setParentForChild(null)}>
                Cancelar
              </button>
              <button type="button" className={btnPrimary} onClick={handleCreateChild}>
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {membersGroupId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div className="max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-lg">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-foreground">Miembros del grupo</h2>
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setMembersGroupId(null)}
              >
                Cerrar
              </button>
            </div>
            {!canManageGroups && (
              <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Solo lectura: no puedes modificar miembros con tu nivel de permiso actual.
              </p>
            )}
            {canManageGroups && pickerError && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {pickerError}
              </p>
            )}
            {canManageGroups && pickerLoading && (
              <p className="text-xs text-muted-foreground">Cargando usuarios…</p>
            )}
            {canManageGroups && (
              <div className="flex flex-wrap gap-2">
                <select
                  className={`${inputClass} min-w-[180px] flex-1 py-2`}
                  value={addUserId}
                  onChange={(e) => setAddUserId(e.target.value)}
                  disabled={pickerLoading}
                >
                  <option value="">Usuario…</option>
                  {pickerUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.email}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`${btnPrimary} px-4 py-2`}
                  onClick={async () => {
                    if (!addUserId || !membersGroupId) return;
                    try {
                      await addGroupMember(membersGroupId, parseInt(addUserId, 10));
                      setAddUserId('');
                      setMembers(await fetchGroupMembers(membersGroupId));
                    } catch (e: unknown) {
                      alert(e instanceof Error ? e.message : 'Error');
                    }
                  }}
                >
                  Añadir
                </button>
              </div>
            )}
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border/80">
              {members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2 px-4 py-3 text-sm">
                  <span className="truncate">{m.user.email}</span>
                  {canManageGroups && (
                    <button
                      type="button"
                      className={btnDanger}
                      onClick={async () => {
                        await removeGroupMember(membersGroupId, m.userId);
                        setMembers(await fetchGroupMembers(membersGroupId));
                      }}
                    >
                      Quitar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
